export function pointAlongRoute(path, fraction) {
    if (!path.length)
        return null;
    if (path.length === 1 || fraction <= 0)
        return path[0] ?? null;
    if (fraction >= 1)
        return path.at(-1) ?? null;
    const lengths = segmentLengths(path);
    const target = totalLength(lengths) * fraction;
    let covered = 0;
    for (const [index, length] of lengths.entries()) {
        const start = path[index];
        const end = path[index + 1];
        if (!start || !end)
            continue;
        if (covered + length < target) {
            covered += length;
            continue;
        }
        return interpolate(start, end, length === 0 ? 0 : (target - covered) / length);
    }
    return path.at(-1) ?? null;
}
export function buildRoutePathIndex(path) {
    const cumulative = [0];
    let running = 0;
    for (const length of segmentLengths(path)) {
        running += length;
        cumulative.push(running);
    }
    return { points: [...path], cumulative, total: running };
}
/**
 * `pointAlongRoute` against a pre-measured route: a binary search over the
 * cumulative distances rather than a walk over every segment, so a 60fps
 * animation can sample it per marker per frame.
 */
export function pointAtRouteFraction(index, fraction) {
    const { points, cumulative, total } = index;
    if (!points.length)
        return null;
    if (points.length === 1 || fraction <= 0)
        return points[0] ?? null;
    if (fraction >= 1)
        return points.at(-1) ?? null;
    if (total <= 0)
        return points[0] ?? null;
    const target = total * fraction;
    let low = 0;
    let high = cumulative.length - 1;
    while (low < high) {
        const middle = (low + high + 1) >> 1;
        if ((cumulative[middle] ?? 0) <= target)
            low = middle;
        else
            high = middle - 1;
    }
    const start = points[low];
    if (!start)
        return null;
    const end = points[low + 1];
    if (!end)
        return start;
    const covered = cumulative[low] ?? 0;
    const length = (cumulative[low + 1] ?? 0) - covered;
    return interpolate(start, end, length === 0 ? 0 : (target - covered) / length);
}
export function completedRoutePath(path, fraction) {
    if (path.length < 2 || fraction <= 0)
        return [];
    if (fraction >= 1)
        return [...path];
    const lengths = segmentLengths(path);
    const target = totalLength(lengths) * fraction;
    const first = path[0];
    if (!first)
        return [];
    const completed = [first];
    let covered = 0;
    for (const [index, length] of lengths.entries()) {
        const start = path[index];
        const end = path[index + 1];
        if (!start || !end)
            continue;
        if (covered + length <= target) {
            completed.push(end);
            covered += length;
            continue;
        }
        completed.push(interpolate(start, end, length === 0 ? 0 : (target - covered) / length));
        break;
    }
    return completed;
}
export function progressFootprintPoints(path, progress, maximum = 18) {
    if (path.length < 2 || progress <= 0 || maximum <= 0)
        return [];
    const safeProgress = Math.min(1, progress);
    const count = Math.min(maximum, Math.max(1, Math.ceil(safeProgress * maximum)));
    return Array.from({ length: count }, (_, index) => {
        const fraction = (safeProgress * (index + 1)) / (count + 1);
        const position = pointAlongRoute(path, fraction);
        if (!position)
            throw new Error("Route progression requires a route point");
        const ahead = pointAlongRoute(path, Math.min(1, fraction + 0.001)) ?? position;
        return { position, fraction, bearing: bearingBetween(position, ahead) };
    });
}
function segmentLengths(path) {
    return path.slice(1).flatMap((point, index) => {
        const start = path[index];
        return start ? [haversine(start, point)] : [];
    });
}
function totalLength(lengths) {
    return lengths.reduce((sum, length) => sum + length, 0);
}
function interpolate(start, end, ratio) {
    return {
        lat: start.lat + (end.lat - start.lat) * ratio,
        lng: start.lng + (end.lng - start.lng) * ratio,
    };
}
function bearingBetween(start, end) {
    const radians = Math.PI / 180;
    const startLat = start.lat * radians;
    const endLat = end.lat * radians;
    const deltaLng = (end.lng - start.lng) * radians;
    const y = Math.sin(deltaLng) * Math.cos(endLat);
    const x = Math.cos(startLat) * Math.sin(endLat) -
        Math.sin(startLat) * Math.cos(endLat) * Math.cos(deltaLng);
    return (Math.atan2(y, x) / radians + 360) % 360;
}
function haversine(a, b) {
    const radians = Math.PI / 180;
    const dLat = (b.lat - a.lat) * radians;
    const dLng = (b.lng - a.lng) * radians;
    const value = Math.sin(dLat / 2) ** 2 +
        Math.cos(a.lat * radians) * Math.cos(b.lat * radians) * Math.sin(dLng / 2) ** 2;
    return 12_742_000 * Math.asin(Math.sqrt(value));
}
//# sourceMappingURL=route-progress.js.map